class MySquishyQuiz extends HTMLElement {
  constructor() {
    super();

    this.state = {
      screen: 0,
      answers: {},
      copied: false,
    };

    this.controller = null;
    this.advanceTimer = null;
    this.loadingTimer = null;
    this.copyTimer = null;
    this.storageKey = '';
  }

  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.storageKey = `ms-dumpling-quiz:${this.dataset.sectionId || 'default'}`;

    document.body.classList.add('ms-quiz-page');
    this.restoreState();
    this.addEventListener('click', this.handleClick, { signal: this.controller.signal });
    this.preserveAdParameters();
    this.render({ focus: false, announce: false });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.clearTimers();

    if (!document.querySelector('my-squishy-quiz')) {
      document.body.classList.remove('ms-quiz-page');
    }
  }

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const startButton = target.closest('[data-ms-quiz-start]');
    if (startButton) {
      this.track('quiz_start');
      this.setScreen(1);
      return;
    }

    const backButton = target.closest('[data-ms-quiz-back]');
    if (backButton) {
      this.clearTimers();
      this.setScreen(Math.max(0, this.state.screen - 1));
      return;
    }

    const answerButton = target.closest('[data-ms-quiz-question][data-ms-quiz-answer]');
    if (answerButton instanceof HTMLButtonElement) {
      this.pickAnswer(answerButton);
      return;
    }

    const addButton = target.closest('[data-ms-quiz-add]');
    if (addButton instanceof HTMLButtonElement) {
      this.addToBasket(addButton);
      return;
    }

    const copyButton = target.closest('[data-ms-quiz-copy]');
    if (copyButton) {
      this.copyDiscountCode();
      return;
    }

    const restartButton = target.closest('[data-ms-quiz-restart]');
    if (restartButton) {
      this.restart();
      return;
    }

    const cta = target.closest('[data-ms-quiz-cta]');
    if (cta) {
      this.track('quiz_cta_click', {
        placement: cta.dataset.msQuizCta,
        product: this.getMatchedProductKey(),
      });
    }
  };

  pickAnswer(button) {
    if (button.disabled) return;

    const question = button.dataset.msQuizQuestion;
    const answer = button.dataset.msQuizAnswer;
    if (!question || !answer) return;

    this.state.answers = { ...this.state.answers, [question]: answer };
    this.persistState();

    const currentScreen = button.closest('[data-ms-quiz-screen]');
    currentScreen?.querySelectorAll('[data-ms-quiz-question]').forEach((candidate) => {
      candidate.disabled = true;
      candidate.classList.toggle('is-selected', candidate === button);
      candidate.setAttribute('aria-pressed', candidate === button ? 'true' : 'false');
    });

    this.clearTimer('advanceTimer');
    this.advanceTimer = window.setTimeout(() => {
      button.classList.remove('is-selected');

      if (question === 'q1' && answer === 'know') {
        this.track('quiz_shop_redirect', { destination: '/collections/all' });
        window.location.assign(this.collectionUrl());
        return;
      }

      if (this.state.screen === 5) {
        this.setScreen(6);
        this.clearTimer('loadingTimer');
        this.loadingTimer = window.setTimeout(() => this.setScreen(7), 1150);
        return;
      }

      this.setScreen(Math.min(5, this.state.screen + 1));
    }, 210);
  }

  async addToBasket(button) {
    if (button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    const label = button.querySelector('[data-ms-quiz-add-label]');
    if (!variantId || !label) return;

    const originalLabel = label.textContent;
    button.disabled = true;
    label.textContent = 'Adding to basket…';

    let cartUpdateDeferred;
    let CartErrorEventClass;

    try {
      const { CartLinesUpdateEvent, CartErrorEvent } = await import('@shopify/events');
      CartErrorEventClass = CartErrorEvent;
      cartUpdateDeferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: cartUpdateDeferred.promise,
        })
      );

      const root = window.Shopify?.routes?.root || '/';
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      const sectionRequest = sectionIds.length
        ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
        : {};
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], ...sectionRequest }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Unable to add this product.');
      }

      let renderedSections = result.sections;
      const discountCode = this.dataset.discountCode?.trim();
      if (discountCode) {
        const discountResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ discount: discountCode, ...sectionRequest }),
        });

        if (discountResponse.ok) {
          const discountResult = await discountResponse.json();
          renderedSections = discountResult.sections || renderedSections;
        }
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh the basket.');

      const ajaxCart = await cartResponse.json();
      cartUpdateDeferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'dumpling-quiz',
          sourceId: this.dataset.sectionId || 'dumpling-quiz',
          itemCount: ajaxCart.item_count,
          sections: renderedSections,
          didError: false,
        },
      });

      label.textContent = 'Added to basket ✓';
      this.querySelector('[data-ms-quiz-status]').textContent = 'Your dumpling was added to the basket.';
      this.track('quiz_add_to_cart', {
        product: this.getMatchedProductKey(),
        variant_id: variantId,
      });
    } catch (error) {
      cartUpdateDeferred?.reject(error);

      if (CartErrorEventClass) {
        this.dispatchEvent(
          new CartErrorEventClass({
            error: error?.message || 'Unable to add this product.',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }

      button.disabled = false;
      label.textContent = 'Try adding again';
      this.querySelector('[data-ms-quiz-status]').textContent = 'The product could not be added. Please try again.';

      window.setTimeout(() => {
        if (!button.disabled) label.textContent = originalLabel;
      }, 2200);
    }
  }

  collectionUrl() {
    const url = new URL('/collections/all', window.location.origin);
    const source = new URLSearchParams(window.location.search);
    const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'];

    allowed.forEach((key) => {
      if (source.has(key)) url.searchParams.set(key, source.get(key));
    });

    return url.toString();
  }

  setScreen(screen) {
    this.state.screen = Math.min(7, Math.max(0, Number(screen) || 0));
    this.state.copied = false;
    this.persistState();
    this.render({ focus: true, announce: true });

    if (this.state.screen >= 1 && this.state.screen <= 5) {
      this.track(`quiz_step_${this.state.screen}`, { step: this.state.screen });
    } else if (this.state.screen === 7) {
      this.track('quiz_result', { product: this.getMatchedProductKey() });
    }
  }

  render({ focus = true, announce = true } = {}) {
    const screen = this.state.screen;
    this.dataset.screen = String(screen);

    this.querySelectorAll('[data-ms-quiz-screen]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.msQuizScreen) !== screen;
    });

    const progress = this.querySelector('[data-ms-quiz-progress]');
    const showProgress = screen >= 1 && screen <= 5;
    if (progress) progress.hidden = !showProgress;

    if (showProgress) {
      const step = this.querySelector('[data-ms-quiz-step]');
      const progressBar = this.querySelector('[data-ms-quiz-progressbar]');
      const progressFill = this.querySelector('[data-ms-quiz-progress-fill]');
      const percentage = `${(screen / 5) * 100}%`;

      if (step) step.textContent = `Step ${screen} of 5`;
      progressBar?.setAttribute('aria-valuenow', String(screen));
      if (progressFill instanceof HTMLElement) progressFill.style.width = percentage;
    }

    this.renderSelections();
    this.renderResult();

    const sticky = this.querySelector('[data-ms-quiz-sticky]');
    if (sticky) sticky.hidden = screen !== 7;

    if (announce) this.announceScreen();
    if (focus) this.focusCurrentHeading();
  }

  renderSelections() {
    this.querySelectorAll('[data-ms-quiz-question][data-ms-quiz-answer]').forEach((button) => {
      const selected = this.state.answers[button.dataset.msQuizQuestion] === button.dataset.msQuizAnswer;
      button.disabled = false;
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  renderResult() {
    const productKey = this.getMatchedProductKey();

    this.querySelectorAll('[data-ms-quiz-product]').forEach((product) => {
      product.hidden = product.dataset.msQuizProduct !== productKey;
    });

    this.querySelectorAll('[data-ms-quiz-sticky-product]').forEach((link) => {
      link.hidden = link.dataset.msQuizStickyProduct !== productKey;
    });

    const copyLabel = this.querySelector('[data-ms-quiz-copy-label]');
    if (copyLabel) copyLabel.textContent = this.state.copied ? 'Copied ✓' : 'Copy';
  }

  getMatchedProductKey() {
    const answers = this.state.answers;
    const glitter = answers.q3 === 'glitter';
    const characters = answers.q3 === 'characters';
    const mystery = answers.q3 === 'mystery';
    const rainbow = answers.q3 === 'rainbow';
    const big = answers.q4 === 'big';
    const set = answers.q4 === 'set';
    const one = answers.q4 === 'one';
    const gift = answers.q2 === 'gift';
    const collection = answers.q2 === 'collection';
    const collector = answers.q5 === 'collector';
    const calm = answers.q5 === 'calm';
    let match = 'classic';

    if ((mystery || rainbow) && (big || collector)) match = 'nine';
    else if (glitter && (set || collection || big)) match = 'glitter';
    else if (characters && (mystery || gift || set)) match = 'character';
    else if (glitter) match = 'glitter';
    else if (one && (characters || calm)) match = 'classic';
    else if (mystery || answers.q1 === 'surprise') match = 'nine';
    else if (big || collector || collection) match = 'nine';
    else if (gift) match = 'character';

    const requestedProduct = this.querySelector(`[data-ms-quiz-product="${match}"]`);
    if (requestedProduct?.dataset.msQuizProductAvailable === 'true') return match;

    const availableFallback = this.querySelector('[data-ms-quiz-product-available="true"]');
    return availableFallback?.dataset.msQuizProduct || match;
  }

  async copyDiscountCode() {
    const code = this.dataset.discountCode?.trim();
    if (!code) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (_error) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.append(textarea);
      textarea.select();
      copied = document.execCommand('copy');
      textarea.remove();
    }

    if (!copied) return;

    this.state.copied = true;
    this.renderResult();
    this.querySelector('[data-ms-quiz-status]').textContent = `Discount code ${code} copied.`;

    this.clearTimer('copyTimer');
    this.copyTimer = window.setTimeout(() => {
      this.state.copied = false;
      this.renderResult();
    }, 2200);
  }

  restart() {
    this.clearTimers();
    this.state = { screen: 0, answers: {}, copied: false };

    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (_error) {
      // Storage can be unavailable in strict privacy contexts.
    }

    this.render({ focus: true, announce: true });
  }

  restoreState() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(this.storageKey));
      const screen = Number(saved?.screen);
      const answers = saved?.answers;

      if (Number.isInteger(screen) && screen >= 0 && screen <= 7 && answers && typeof answers === 'object') {
        this.state.screen = screen === 6 ? 7 : screen;
        this.state.answers = answers;
      }
    } catch (_error) {
      this.state = { screen: 0, answers: {}, copied: false };
    }
  }

  persistState() {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({ screen: this.state.screen, answers: this.state.answers })
      );
    } catch (_error) {
      // The quiz remains fully functional when storage is unavailable.
    }
  }

  preserveAdParameters() {
    const source = new URLSearchParams(window.location.search);
    const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'];
    const values = allowed.filter((key) => source.has(key));
    if (!values.length) return;

    this.querySelectorAll('[data-ms-quiz-cta]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const url = new URL(link.href, window.location.origin);
      values.forEach((key) => url.searchParams.set(key, source.get(key)));
      link.href = url.toString();
    });
  }

  announceScreen() {
    const status = this.querySelector('[data-ms-quiz-status]');
    if (!status) return;

    if (this.state.screen >= 1 && this.state.screen <= 5) {
      status.textContent = `Quiz step ${this.state.screen} of 5.`;
    } else if (this.state.screen === 6) {
      status.textContent = 'Finding your dumpling match.';
    } else if (this.state.screen === 7) {
      status.textContent = 'Your dumpling match is ready.';
    } else {
      status.textContent = 'Dumpling quiz introduction.';
    }
  }

  focusCurrentHeading() {
    window.requestAnimationFrame(() => {
      const currentScreen = this.querySelector(`[data-ms-quiz-screen="${this.state.screen}"]`);
      const heading = currentScreen?.querySelector('h1, h2');
      heading?.focus({ preventScroll: true });
    });
  }

  track(eventName, detail = {}) {
    const payload = {
      event: eventName,
      quiz_id: this.dataset.sectionId || 'dumpling-quiz',
      ...detail,
    };

    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  }

  clearTimer(name) {
    if (!this[name]) return;
    window.clearTimeout(this[name]);
    this[name] = null;
  }

  clearTimers() {
    this.clearTimer('advanceTimer');
    this.clearTimer('loadingTimer');
    this.clearTimer('copyTimer');
  }
}

if (!customElements.get('my-squishy-quiz')) {
  customElements.define('my-squishy-quiz', MySquishyQuiz);
}
